/**
 * track:project-background-page — the single source of truth for reading
 * and writing a project's background (Setup/Background/Requirements + PRD
 * versioning). Both ProjectBackgroundModal's wiring in src/App.tsx and the
 * standalone ProjectBackgroundPage.tsx call these two functions — neither
 * keeps its own independent fetch/cache logic, so an edit made through
 * either surface hits the same Firestore doc and REST endpoint and is
 * immediately visible through the other.
 *
 * Pulled out of App.tsx rather than passed down as bound callbacks because
 * App.tsx's existing openExistingBackground/saveExistingBackground close
 * over `pendingViewProject` — the modal's single-project-at-a-time slot.
 * ProjectBackgroundPage needs several projects' data loaded at once (one
 * per expanded accordion), so it can't safely share those exact closures
 * (doing so would either open the modal unexpectedly or let an unrelated
 * project's save overwrite whatever the modal currently has open — see PR
 * description for the concrete case). These two functions have no such
 * coupling: they only talk to the backend and return data.
 */
import { getAuthHeader } from "./auth";
import { slugify, type ProjectBackground } from "./projectsMigration";

/**
 * track:project-record-p2 (spec #49079 item 4) — every write must carry the
 * `version` last read. Tracked here, in the one module both the modal and the
 * standalone page save through, so neither surface can forget to send it: a
 * fetch records the doc's version, a successful save records the new one.
 */
const knownVersions = new Map<string, number>();

/**
 * GET /api/workspace/project-background/:slug. Returns `fallback` (e.g. the
 * project's last-known background from the `projects` array) if the fetch
 * fails or the doc doesn't exist yet.
 */
export async function fetchProjectBackground(
  name: string,
  fallback?: ProjectBackground,
): Promise<ProjectBackground | undefined> {
  const slug = slugify(name);
  try {
    const headers = await getAuthHeader();
    const res = await fetch(`/api/workspace/project-background/${encodeURIComponent(slug)}`, { headers });
    const data = await res.json();
    if (res.ok && data.background) {
      const bg = data.background as ProjectBackground;
      if (typeof bg.version === "number") knownVersions.set(slug, bg.version);
      return bg;
    }
  } catch (err) {
    console.error("[Relay] project background load failed:", err);
  }
  return fallback;
}

export interface SaveProjectBackgroundResult {
  prd_version?: number;
  prd_label?: string;
  version?: number;
}

/** POST /api/workspace/project-background. Throws on a non-OK response. */
export async function saveProjectBackground(
  name: string,
  background: ProjectBackground,
  opts: { versionedTab?: "background" | "requirements" } = {},
): Promise<SaveProjectBackgroundResult> {
  const slug = slugify(name);
  const headers = { ...(await getAuthHeader()), "Content-Type": "application/json" };
  const res = await fetch("/api/workspace/project-background", {
    method: "POST",
    headers,
    body: JSON.stringify({ projectId: slug, name, ...background, ...opts, version: knownVersions.get(slug) ?? background.version }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 409) {
    throw new Error("This project was changed by someone else since you opened it. Close and reopen it to load the latest, then re-apply your edit.");
  }
  if (!res.ok) throw new Error(data.message || data.error || "Save failed.");
  if (typeof data.version === "number") knownVersions.set(slug, data.version);
  return data;
}
